import type { SupabaseClient } from '@supabase/supabase-js'

/**
 * 현재 로그인한 사용자의 활성 프로필 ID를 조회합니다.
 * 
 * @param supabase - Supabase 클라이언트 인스턴스
 * @param userId - auth.users.id (선택사항, 없으면 자동 조회)
 * @returns 활성 프로필의 profile_id
 * @throws Error - 프로필을 찾을 수 없거나 비활성 상태인 경우
 * 
 * @example
 * ```typescript
 * const supabase = createBrowserClient()
 * const { data: { user } } = await supabase.auth.getUser()
 * const profileId = await requireActiveProfileId(supabase, user.id)
 * 
 * await supabase.from('estimates').insert({ // project_members 대신 estimates 사용
 *   counsel_id: 123,
 *   profile_id: profileId, // ✅ 올바른 사용
 *   role: 'MAKER'
 * })
 * ```
 */
export async function requireActiveProfileId(
  supabase: SupabaseClient<any, 'public', any>,
  userId?: string
): Promise<string> {
  // userId가 제공되지 않으면 현재 세션에서 조회
  let finalUserId = userId
  if (!finalUserId) {
    const { data: { user }, error: authError } = await supabase.auth.getUser()
    if (authError || !user) {
      throw new Error('인증되지 않은 사용자입니다.')
    }
    finalUserId = user.id
  }

  const { data, error } = await supabase
    .from('accounts')
    .select('profile_id')
    .eq('user_id', finalUserId)
    .is('deleted_at', null)
    // .eq('is_active', true)
    .maybeSingle()

  if (error) {
    throw new Error(`프로필 조회 실패: ${error.message}`)
  }

  if (!data?.profile_id) {
    throw new Error('활성 프로필을 찾을 수 없습니다. 프로필을 먼저 생성해주세요.')
  }

  return data.profile_id as string
}

/**
 * 현재 로그인한 사용자의 프로필 ID를 조회합니다 (없어도 null 반환)
 * 
 * @param supabase - Supabase 클라이언트 인스턴스
 * @param userId - auth.users.id (선택사항)
 * @returns 활성 프로필의 profile_id 또는 null
 */
export async function getActiveProfileId(
  supabase: SupabaseClient,
  userId?: string
): Promise<string | null> {
  try {
    return await requireActiveProfileId(supabase, userId)
  } catch {
    return null
  }
}

/**
 * 서버 사이드 전용: auth user id로부터 profile_id를 조회합니다.
 * API route에서 사용하는 헬퍼 함수입니다.
 * 
 * @param supabase - 서버 사이드 Supabase 클라이언트
 * @param authUserId - auth.users.id (필수)
 * @returns 활성 프로필의 profile_id
 * @throws Error - 프로필을 찾을 수 없거나 비활성 상태인 경우
 * 
 * @example
 * ```typescript
 * // API route에서 사용
 * import { createServerSideClient } from '@/supabase/supabase-server'
 * import { getActiveProfileIdFromAuth } from '@/utils/profile-helpers'
 * 
 * const supabase = await createServerSideClient()
 * const { data: { user } } = await supabase.auth.getUser()
 * const profileId = await getActiveProfileIdFromAuth(supabase, user.id)
 * ```
 */// src/utils/profile-helpers.ts

 type GetActiveProfileOpts = { allowMissing?: boolean }
 
 // ✅ 오버로드: (supabase, userId, opts?) 또는 (supabase, opts?)
 export async function getActiveProfileIdFromAuth(
   supabase: SupabaseClient,
   authUserId: string,
   opts?: GetActiveProfileOpts
 ): Promise<string>
 export async function getActiveProfileIdFromAuth(
   supabase: SupabaseClient,
   opts?: GetActiveProfileOpts
 ): Promise<string>
 
 // ✅ 구현
 export async function getActiveProfileIdFromAuth(
   supabase: SupabaseClient,
   userIdOrOpts?: string | GetActiveProfileOpts,
   maybeOpts?: GetActiveProfileOpts
 ) {
   const opts: GetActiveProfileOpts | undefined =
     typeof userIdOrOpts === 'string' ? maybeOpts : userIdOrOpts
 
   const authUserId =
     typeof userIdOrOpts === 'string'
       ? userIdOrOpts
       : (await supabase.auth.getUser()).data.user?.id
 
   if (!authUserId) {
     throw new Error('UNAUTHORIZED')
   }
 
   const { data, error } = await supabase
     .from('accounts')
     .select('profile_id')
     .eq('user_id', authUserId)
    //  .eq('is_active', true)
     .is('deleted_at', null)
     .maybeSingle()
 
   if (error) throw error
 
   if (!data?.profile_id) {
     if (opts?.allowMissing) return null
     throw new Error('PROFILE_NOT_FOUND')
   }
 
   return data.profile_id as string
 }